import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { EnumDropdownItemVariant, showToast, type IDropdownItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { TransferApi } from 'entities/Transfer'
import { User } from 'entities/Users/model/User'
import {
  type IParamsConversationsTransferAssignee,
  type IParamsConversationsTransfer,
} from 'entities/Transfer/api/types'
import { type IResponseOrganization } from 'entities/Organization/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TransferConversationModalContent } from '../ui/TransferConversationModalContent'
import { TransferConversationModalActions } from '../ui/TransferConversationModalActions'

export class TransferConversationModalStore {
  private _loading = false
  private _conversationIds: number[] = []
  private _bulkAll = false
  private _conversationsFilter = ''
  private _note = ''
  private _noteError = ''

  // Organizations
  private _organizationsMap = new Map<number, IResponseOrganization>()
  private _activeOrganizationId: number | null = null
  private _loadingOrganizations = true
  private _searchOrganization = ''
  private _organizationError = ''

  // Inboxes
  private _inboxesMap = new Map<number, Inbox>()
  private _searchInboxes = ''
  private _activeInboxId: number | null = null
  private _loadingInboxes = true
  private _inboxError = ''

  // Members
  private _membersMap = new Map<number, User>()
  private _searchMembers = ''
  private _activeMemberId: number | null = 0
  private _loadingMembers = true

  private _disposeOrganizations: IReactionDisposer | null = null
  private _disposeActiveOrganization: IReactionDisposer | null = null
  private _disposeActiveInbox: IReactionDisposer | null = null

  private _modalId = ''

  private readonly _requestCancelAxios = new RequestCancelAxios()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._organizationsReaction()
    this._activeOrganizationReaction()
    this._activeInboxReaction()
    this.loadOrganizations()
  }

  get noteError() {
    return this._noteError
  }

  get loadingInboxes() {
    return this._loadingInboxes
  }

  get organizationError() {
    return this._organizationError
  }

  get inboxError() {
    return this._inboxError
  }

  get note() {
    return this._note
  }

  get conversationIds() {
    return this._conversationIds
  }

  get loading() {
    return this._loading
  }

  get loadingMembers() {
    return this._loadingMembers
  }

  get activeOrganizationId() {
    return this._activeOrganizationId
  }

  get activeMemberId() {
    return this._activeMemberId
  }

  get activeMember() {
    if (!this._activeMemberId) return 0

    return this._membersMap.get(this._activeMemberId)
  }

  get searchMembers() {
    return this._searchMembers
  }

  get activeOrganization() {
    if (!this._activeOrganizationId) return null

    return this._organizationsMap.get(this._activeOrganizationId)
  }

  get searchOrganization() {
    return this._searchOrganization
  }

  get isSingleOrganization() {
    return this.organizations.length === 1
  }

  get activeInboxId() {
    return this._activeInboxId
  }

  get activeInbox() {
    if (!this._activeInboxId) return null

    return this._inboxesMap.get(this._activeInboxId)
  }

  get searchInboxes() {
    return this._searchInboxes
  }

  get inboxes() {
    return Array.from(this._inboxesMap.values()).filter(
      (inbox) => inbox.id !== this._entities.inboxesStore.currentInboxId
    )
  }

  get members() {
    return Array.from(this._membersMap.values())
  }

  setNote = (value: string) => {
    this._noteError = ''
    this._note = value
  }

  setActiveOrganizationId = (id: number | string) => {
    this._organizationError = ''
    this._activeOrganizationId = Number(id)
  }

  setSearchOrganization = (value: string) => {
    this._searchOrganization = value
  }

  setSearchMembers = (value: string) => {
    this._searchMembers = value
  }

  setActiveMemberId = (id: number | string) => {
    this._activeMemberId = Number(id)
  }

  setActiveInboxId = (id: number | string) => {
    this._inboxError = ''
    this._activeInboxId = Number(id)
  }

  setSearchInboxes = (value: string) => {
    this._searchInboxes = value
  }

  setConversationIds = (ids: number[]) => {
    this._conversationIds = ids
  }

  reset = () => {
    this._conversationIds = []
    this._activeOrganizationId = null
    this._searchOrganization = ''
    this._searchMembers = ''
    this._activeMemberId = null
    this._membersMap.clear()
    this._searchInboxes = ''
    this._activeInboxId = null
    this._inboxesMap.clear()
    this._organizationError = ''
    this._inboxError = ''
    this._note = ''
    this._bulkAll = false
    this._conversationsFilter = ''
    this._noteError = ''
  }

  searchValue = (value: string) => {
    return value.toLocaleLowerCase().replaceAll(/\s/g, '')
  }

  get organizations() {
    return Array.from(this._organizationsMap.values())
  }

  get organizationsDropdownItems() {
    const data = this.organizations.map((organization) => {
      const itemInfo: IDropdownItem = {
        id: `${organization.id}`,
        label: organization.name,
        activeValue: this._activeOrganizationId === organization.id,
        variant: EnumDropdownItemVariant.CheckedRight,
      }

      return itemInfo
    })

    if (this._searchOrganization) {
      const search = this.searchValue(this._searchOrganization)
      return data.filter((item) => this.searchValue(item.label).includes(search))
    }

    return data
  }

  get inboxesDropdownItems() {
    const data = this.inboxes.map((inbox) => {
      const itemInfo: IDropdownItem = {
        id: `${inbox.id}`,
        label: inbox.name,
        iconL: 'inboxOpen',
        activeValue: this._activeInboxId === inbox.id,
        variant: EnumDropdownItemVariant.CheckedRight,
      }

      return itemInfo
    })

    if (this._searchInboxes) {
      const search = this.searchValue(this._searchInboxes)
      return data.filter((item) => this.searchValue(item.label).includes(search))
    }

    return data
  }

  get membersDropdownItems() {
    const data = this.members.map((member) => {
      const itemInfo: IDropdownItem = {
        id: `${member.id}`,
        label: member.name,
        avatarProps: {
          info: {
            image: member.photo_url || '',
            color: '',
            firstName: member.name,
          },
          online: !member.isAway,
          size: 16,
          iconProps: {
            fontSize: 12,
          },
        },
        activeValue: this._activeMemberId === member.id,
        variant: EnumDropdownItemVariant.CheckedRight,
      }

      return itemInfo
    })

    data.unshift({
      id: 0,
      label: 'Inbox default settings',
      iconL: 'settingsLine',
      activeValue: this.activeMemberId === 0,
      variant: EnumDropdownItemVariant.CheckedRight,
    })

    if (this._searchMembers) {
      const search = this._searchMembers.toLocaleLowerCase().replaceAll(/\s/g, '')
      return data.filter((item) => item.label.toLocaleLowerCase().includes(search))
    }

    return data
  }

  get isEmptyMembersDropdown() {
    return this.membersDropdownItems.length === 0
  }

  get isEmptyInboxesDropdown() {
    return this.inboxesDropdownItems.length === 0
  }

  get isEmptyOrganizationsDropdown() {
    return this.organizationsDropdownItems.length === 0
  }

  loadOrganizations = async () => {
    try {
      this._loadingOrganizations = true

      if (this._entities.organizationStore.id && this._entities.organizationStore.original) {
        this._organizationsMap.set(
          this._entities.organizationStore.id,
          this._entities.organizationStore.original
        )
      }

      if (!this._entities.organizationStore.isHipaaCompliant) {
        this._requestCancelAxios.init()

        const responseData = await TransferApi.getActiveOrganizations({
          cancelToken: this._requestCancelAxios.token,
        })
        const data = responseData.data

        data.forEach((organization) => {
          runInAction(() => {
            this._organizationsMap.set(organization.id, organization)
          })
        })
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loadingOrganizations = false
      })
    }
  }

  loadInboxes = async () => {
    if (!this._activeOrganizationId) return

    try {
      this._loadingInboxes = true
      const { data } = await TransferApi.getTeamsByOrganizationId(this._activeOrganizationId)
      data.forEach((inbox) => {
        if (inbox.type === 'inbox') this._inboxesMap.set(inbox.id, new Inbox(inbox, this._entities))
      })
    } catch (error) {
      logger.error(error)
    } finally {
      this._loadingInboxes = false
    }
  }

  loadMembers = async () => {
    if (!this._activeInboxId) return

    try {
      this._loadingMembers = true
      const { data } = await TransferApi.getMembersByInboxId(this._activeInboxId)
      data.forEach((user) => {
        this._membersMap.set(user.id, new User(user))
      })
    } catch (error) {
      logger.error(error)
    } finally {
      this._loadingMembers = false
    }
  }

  private _isValid = () => {
    let isValid = true

    if (!this._activeOrganizationId) {
      this._organizationError = 'Please select organization'
      isValid = false
    }

    if (!this._activeInboxId) {
      this._inboxError = 'Please select inbox'
      isValid = false
    }

    return isValid
  }

  onTransfer = async () => {
    if (!this._isValid()) return

    const assignee: IParamsConversationsTransferAssignee = this._activeMemberId
      ? { type: 'member', member_id: this._activeMemberId || 0 }
      : { type: 'default' }

    const data: IParamsConversationsTransfer = {
      organization_id: this._activeOrganizationId || 0,
      bulk_all: this._bulkAll,
      conversation_filter: this._conversationsFilter,
      team_id: this.activeInboxId || 0,
      assignee,
      note: this._note,
      conversation_ids: this._conversationIds,
      target_team_id: this._entities.inboxesStore.currentInboxId || null,
    }

    try {
      this._loading = true

      await TransferApi.transferConversations(data)

      showToast({
        title: `Conversations successfully transferred to ${this.activeInbox?.name}`,
        type: 'success',
      })
      this.reset()
      modalStore.removeModal(this._modalId)
    } catch (e) {
      if (e instanceof AxiosError) {
        this._noteError = e.errors.getError('note') || ''
        this._inboxError = e.errors.getError('team_id') || ''
      }

      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  openModal = (
    ids: number[],
    store: TransferConversationModalStore,
    bulkAll: boolean,
    conversationsFilter: string
  ) => {
    this._bulkAll = bulkAll
    this._conversationsFilter = conversationsFilter
    this._conversationIds = ids
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      type: ModalTypeList.DEFAULT,
      ModalContentProps: { ids, store },
      paddingContent: '0 24px 16px',
      ModalContent: () => <TransferConversationModalContent store={store} />,
      ModalActions: () => <TransferConversationModalActions store={store} />,
      title: 'Transfer conversation',
      onClose: this.onCloseModal,
    })
  }

  onCloseModal = () => {
    modalStore.removeModal(this._modalId)
    this.reset()
  }

  private _organizationsReaction = () => {
    this._disposeOrganizations?.()

    this._disposeOrganizations = reaction(
      () => [this.isSingleOrganization, this._loadingOrganizations, this._modalId],
      ([isSingleOrganization, loadingOrganizations, modalId]) => {
        if (isSingleOrganization && !loadingOrganizations && modalId) {
          this.setActiveOrganizationId(this.organizations[0].id)
        }
      }
    )
  }

  private _activeOrganizationReaction = () => {
    this._disposeActiveOrganization?.()

    this._disposeActiveOrganization = reaction(
      () => this.activeOrganizationId,
      (value) => {
        if (value) {
          this._inboxesMap.clear()
          this._activeInboxId = null
          this._searchInboxes = ''
          this.loadInboxes()
          this._membersMap.clear()
          this._activeMemberId = 0
          this._searchMembers = ''
        }
      }
    )
  }

  private _activeInboxReaction = () => {
    this._disposeActiveInbox?.()

    this._disposeActiveInbox = reaction(
      () => this.activeInboxId,
      (value) => {
        if (value) {
          this._membersMap.clear()
          this._activeMemberId = 0
          this._searchMembers = ''
          this.loadMembers()
        }
      }
    )
  }
}
